import { humanizeBrandName } from "@/features/opportunities/lib/format-opportunity.js";
import { adPlatformLabel } from "@/lib/ad-platform-labels.js";

import type { HubChromeSet } from "./hub-chrome.types.js";
import type { HubOpportunitySort } from "./hub-opportunity-table.js";

export const HUB_SET_SORT_COLUMNS = [
  "name",
  "type",
  "platform",
  "status",
  "window",
  "opportunities",
  "scripts",
  "briefs",
  "sent",
] as const;

export type HubSetSortColumn = (typeof HUB_SET_SORT_COLUMNS)[number];

/** Same two buckets the Sets table prints: competitor, or personas (including manual). */
function setTypeSortKey(set: HubChromeSet): string {
  return set.type === "COMPETITOR" ? "competitor" : "persona";
}

function setSortValue(
  set: HubChromeSet,
  columnId: string,
  t: (key: string, defaultValue: string) => string,
): string | number | null {
  switch (columnId) {
    case "name":
      return set.name;
    case "type":
      return setTypeSortKey(set);
    case "platform":
      return set.adPlatform
        ? adPlatformLabel(t, set.adPlatform, humanizeBrandName(set.adPlatform.toLowerCase()))
        : null;
    case "status":
      return set.status.toLowerCase();
    case "window":
      return set.startDate && set.endDate ? `${set.startDate}\u0000${set.endDate}` : null;
    case "opportunities":
      return set.opportunityCount;
    case "scripts":
      return set.scriptCount;
    case "briefs":
      return set.briefCount;
    case "sent":
      return set.sentCount;
    default:
      return null;
  }
}

/** Blank values sink in both directions, matching the other hub tables. */
export function sortHubSets(
  sets: readonly HubChromeSet[],
  sort: HubOpportunitySort | null,
  t: (key: string, defaultValue: string) => string,
): readonly HubChromeSet[] {
  if (!sort) return sets;
  const factor = sort.direction === "asc" ? 1 : -1;

  return [...sets].sort((left, right) => {
    const a = setSortValue(left, sort.columnId, t);
    const b = setSortValue(right, sort.columnId, t);
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === "number" && typeof b === "number") return (a - b) * factor;
    return String(a).localeCompare(String(b)) * factor;
  });
}
